/**
 * KAN-678: данные окна отправки Telegram-рассылки для страницы кампаний.
 *
 * Откуда операторы с каналом telegram_user (без нового эндпоинта):
 *  - `GET /telegram-accounts/` - аккаунты видимых операторов (оператор видит
 *    свои, владелец с users.view - всей организации); из них берутся
 *    уникальные operator_id/operator_email;
 *  - `GET /users/{me}/` - сам текущий пользователь, если у него есть канал
 *    telegram_user (даже без подключённых аккаунтов).
 * Окно выбранного оператора: `GET /users/{id}/` -> channels[telegram_user].values
 * (там же channels[voice] для окна обзвона). Часовой пояс: window.timezone из
 * `GET /dashboard/outreach-status/` (кэш общий с дашбордом), иначе Europe/Moscow.
 *
 * Сохранение: PUT /users/{id}/channels/telegram_user/ с полными values без
 * масок секретов (см. lib/outreachWindow.buildSaveValues) и прежним `enabled`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api/client';
import { usersApi } from '@/services/api/users';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import { OUTREACH_STATUS_QUERY_KEY, getOutreachStatus } from '@/services/api/dashboard';
import {
  TELEGRAM_USER_CODENAME,
  VOICE_CODENAME,
  buildSaveValues,
  findChannel,
  limitBounds,
  readOutreachWindow,
  readVoiceWindow,
  type NumberBounds,
  type OutreachWindowValues,
  type VoiceWindowValues,
} from '@/lib/outreachWindow';
import { userDisplayName } from '@/lib/cabinetIdentity';
import type { User, UserChannel } from '@/types/user';

const FALLBACK_ZONE = 'Europe/Moscow';

export interface OutreachOperator {
  id: string;
  label: string;
}

export interface OutreachWindowState {
  /** Операторы с каналом telegram_user; пусто - бар не показывается. */
  operators: OutreachOperator[];
  selectedOperatorId: string | null;
  setSelectedOperatorId: (id: string) => void;
  /** Загрузка списка операторов или карточки выбранного оператора. */
  isLoading: boolean;
  /** Канал telegram_user выбранного оператора (undefined, пока карточка не загружена). */
  channel: UserChannel | undefined;
  window: OutreachWindowValues | null;
  voiceWindow: VoiceWindowValues | null;
  limitBounds: NumberBounds;
  timezone: string;
  /** Текущий пользователь может править окно выбранного оператора (self или users.manage). */
  canEdit: boolean;
  save: (values: OutreachWindowValues) => Promise<unknown>;
  isSaving: boolean;
}

interface CurrentUser {
  id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  permissions?: { users?: { manage?: boolean } };
}

export const USER_CHANNELS_QUERY_KEY = 'user-channels';

export function useOutreachWindow(): OutreachWindowState {
  const queryClient = useQueryClient();

  const { data: currentUser } = useQuery<CurrentUser>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/auth/me/');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const currentUserId = currentUser?.id ?? null;
  const canManage = currentUser?.permissions?.users?.manage === true;

  const accountsQuery = useQuery({
    queryKey: ['telegram-accounts'],
    queryFn: telegramAccountsApi.list,
    staleTime: 60 * 1000,
  });

  const selfQuery = useQuery<User>({
    queryKey: [USER_CHANNELS_QUERY_KEY, currentUserId],
    queryFn: () => usersApi.getById(currentUserId as string),
    enabled: currentUserId !== null,
    staleTime: 60 * 1000,
  });

  const operators = useMemo<OutreachOperator[]>(() => {
    const byId = new Map<string, OutreachOperator>();
    if (currentUser && findChannel(selfQuery.data?.channels, TELEGRAM_USER_CODENAME)) {
      byId.set(currentUser.id, { id: currentUser.id, label: userDisplayName(currentUser) });
    }
    for (const account of accountsQuery.data ?? []) {
      if (!byId.has(account.operator_id)) {
        byId.set(account.operator_id, {
          id: account.operator_id,
          label: account.operator_email,
        });
      }
    }
    return Array.from(byId.values());
  }, [currentUser, selfQuery.data, accountsQuery.data]);

  const [chosenId, setChosenId] = useState<string | null>(null);
  // Выбор сбрасывается, если выбранный оператор пропал из списка.
  useEffect(() => {
    if (chosenId && !operators.some((o) => o.id === chosenId)) setChosenId(null);
  }, [chosenId, operators]);

  const selectedOperatorId =
    chosenId ??
    (currentUserId && operators.some((o) => o.id === currentUserId)
      ? currentUserId
      : (operators[0]?.id ?? null));

  const operatorQuery = useQuery<User>({
    queryKey: [USER_CHANNELS_QUERY_KEY, selectedOperatorId],
    queryFn: () => usersApi.getById(selectedOperatorId as string),
    enabled: selectedOperatorId !== null,
    staleTime: 60 * 1000,
  });

  const statusQuery = useQuery({
    queryKey: OUTREACH_STATUS_QUERY_KEY,
    queryFn: getOutreachStatus,
    staleTime: 60 * 1000,
  });
  const timezone = statusQuery.data?.window?.timezone || FALLBACK_ZONE;

  const channel = findChannel(operatorQuery.data?.channels, TELEGRAM_USER_CODENAME);
  const voiceChannel = findChannel(operatorQuery.data?.channels, VOICE_CODENAME);
  // Объекты окна мемоизированы: диалог сбрасывает форму по смене ссылки.
  const windowValues = useMemo(
    () => (channel ? readOutreachWindow(channel) : null),
    [channel]
  );
  const voiceWindow = useMemo(() => readVoiceWindow(voiceChannel), [voiceChannel]);
  const bounds = useMemo(() => limitBounds(channel), [channel]);

  const mutation = useMutation({
    mutationFn: (values: OutreachWindowValues) => {
      if (!selectedOperatorId || !channel) {
        return Promise.reject(new Error('Канал Telegram не загружен'));
      }
      return usersApi.updateChannel(selectedOperatorId, TELEGRAM_USER_CODENAME, {
        enabled: channel.enabled,
        values: buildSaveValues(channel, values),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [USER_CHANNELS_QUERY_KEY, selectedOperatorId] });
      void queryClient.invalidateQueries({ queryKey: ['currentUserChannels'] });
      void queryClient.invalidateQueries({ queryKey: OUTREACH_STATUS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
    },
  });

  const { mutateAsync } = mutation;
  const save = useCallback(
    (values: OutreachWindowValues) => mutateAsync(values),
    [mutateAsync]
  );

  const isLoading =
    accountsQuery.isLoading ||
    (currentUserId !== null && selfQuery.isLoading) ||
    (selectedOperatorId !== null && operatorQuery.isLoading);

  return {
    operators,
    selectedOperatorId,
    setSelectedOperatorId: setChosenId,
    isLoading,
    channel,
    window: windowValues,
    voiceWindow,
    limitBounds: bounds,
    timezone,
    canEdit: Boolean(selectedOperatorId) && (canManage || selectedOperatorId === currentUserId),
    save,
    isSaving: mutation.isPending,
  };
}
